import React from 'react';
import { Box, Typography, Card, CardMedia, Divider } from '@mui/material';
import { SPACING, RADIUS } from '../../../theme/theme';

interface ApplicationBoxProps {
  nameFa: string; // Persian title of the application
  nameEn: string; // English title of the application
  version: string; // Version of the application
  downloadCountText: string; // Text for the download count
  totalPrice: number | string; // Total price of the application
  logo?: string | null; // URL of the application logo
  CustomButton: React.ReactNode; // Custom button component
  InterButton: React.ReactNode; // Internal button component
}

const ApplicationBox: React.FC<ApplicationBoxProps> = ({
  nameFa,
  nameEn,
  version,
  downloadCountText,
  totalPrice,
  logo,
  CustomButton,
  InterButton,
}) => {
  return (
    <Card
      elevation={0}
      sx={{
        // Fill the grid column (three per row) instead of a fixed width that
        // the column would clip anyway.
        width: '100%',
        p: `${SPACING.lg}px`,
        backgroundColor: 'white',
        // Rounded, shadowed panel instead of a hard 1px box.
        borderRadius: `${RADIUS.lg}px`,
        border: 'none',
        boxShadow: '0 2px 10px rgba(16, 24, 40, 0.06)',
        direction: 'rtl',
        display: 'flex',
        flexDirection: 'column',
        gap: `${SPACING.md}px`,
        transition: 'box-shadow .2s ease, transform .2s ease',
        '&:hover': {
          boxShadow: '0 6px 18px rgba(16, 24, 40, 0.10)',
          transform: 'translateY(-2px)',
        },
        '@media (max-width: 350px)': {
          width: '100%',
        },
      }}
    >
      {/* Header: icon on the right, title/version beside it, status on the left */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: `${SPACING.sm}px`,
        }}
      >
        <CardMedia
          component="img"
          sx={{
            width: 76,
            height: 76,
            flexShrink: 0,
            borderRadius: `${RADIUS.lg}px`,
            objectFit: 'cover',
            backgroundColor: '#EDEDF0',
          }}
          src={logo || 'https://via.placeholder.com/76x76'}
          alt="App Icon"
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            title={`${nameFa} | ${nameEn}`}
            sx={{
              fontWeight: 700,
              fontFamily: 'IRANYekanXFaNum',
              fontSize: 13,
              lineHeight: '18px',
              textAlign: 'right',
              color: '#25282E',
              // Long names truncate instead of pushing the badge off the card.
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden',
            }}
          >
            {`${nameFa} | ${nameEn}`}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#A6AAB5',
              fontFamily: 'IRANYekanXFaNum',
              fontWeight: 500,
              fontSize: 11,
              textAlign: 'right',
              mt: `${SPACING.sm}px`,
            }}
          >
            نسخه {version}
          </Typography>
        </Box>

        {/* Badge sits at the top-left, as in the design. */}
        <Box sx={{ display: 'flex', alignSelf: 'flex-start', flexShrink: 0 }}>
          {CustomButton}
        </Box>
      </Box>

      <Divider sx={{ borderColor: '#EDEDF0' }} />

      {/* Footer: entry link. The card is RTL, so flex-end is its left edge. */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        {InterButton}
      </Box>
    </Card>
  );
};

export default ApplicationBox;
